import {
  memo,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
  type WheelEvent,
} from "react";
import { ArrowUpRight, ChevronLeft, ChevronRight, Pause, Play, Volume2, VolumeX } from "lucide-react";
import { cn } from "@/utils/helpers";
import type { TrendExample } from "@/types/trend.type";
import { PlateMedia, type PlateArt } from "@/app/routes/dashboard/components/Plate";
import PlatformBadge from "@/app/routes/dashboard/components/PlatformBadge";
import PlatformGlyph, { hasGlyph } from "@/app/routes/dashboard/components/PlatformGlyph";
import { usePlayback } from "@/app/routes/dashboard/components/mediaPlayback";
import { compactTrendNum, platformLabel, spokenTrendNum } from "./trendMeta";

/**
 * The examples a trend was built from, as the detail sheet's two columns.
 *
 * THE STAGE COLUMN is one scroll-snap track with one example per stop, the
 * media chips the wall plate wears (the platform of the example on screen, and
 * sound) plus the stage's own Pause, and one row under the frame: the example
 * bars, EXAMPLE i OF n, and the arrows. On a phone the track is full bleed and
 * the finger moves it. Every clip is fitted whole inside the stage, never cut.
 *
 * THE READING COLUMN is the sheet's, not the carousel's. The panel hands in the
 * trend-level blocks as `lead` (title through EVIDENCE) and `tail` (WHERE IT
 * WAS FOUND) and the carousel puts THIS EXAMPLE between them, because it is the
 * one block that changes with the stage. `lead` and `tail` are elements the
 * panel built once, so a swipe re-renders this component without reconciling
 * either of them.
 *
 * THE READING CHANGES ONCE, AT THE DESTINATION. The position is read from the
 * scroll offset, which reports continuously, so there are two positions here.
 * The live one moves the bars, the counter and decides which arrow is spent,
 * because a control that lags the finger feels broken. The SETTLED one,
 * committed on scrollend where the platform has it and on a short timer where
 * it does not, is what THIS EXAMPLE, the stage chips, the polite announcement
 * and the playing slide are drawn from, so they change once, together, when the
 * carousel lands.
 *
 * WHAT IT WILL NOT SAY, per example:
 *
 *  - A null viewCount or likeCount is NOT COUNTED, never a zero.
 *  - A null pageUrl gets no link, never a dead one.
 *  - A missing handle or caption is said to be missing; nothing is put in its
 *    place.
 *  - Nothing playable means the explicit "no preview captured" face.
 *  - AND THE CAP. The detail route answers with at most six examples from the
 *    latest observation, while the trend's exampleCount aggregates every one of
 *    them. Where the two disagree the note under the counter says so, because a
 *    carousel that ends at six silently reads as the complete set.
 */

/** The cap the detail route answers with: TrendExampleRepository
 *  .listLatestForTrend(id, 6). Named rather than inlined so the sentence that
 *  discloses it and the number it discloses can never drift apart. */
const DETAIL_CAP = 6;

/** How long after the last scroll event the carousel is treated as landed, where
 *  the browser has no scrollend to say so itself. */
const SETTLE_MS = 140;

/**
 * What this carousel can and cannot vouch for about being the whole set. At the
 * cap the cap is the reason and is named; a short list that still misses
 * examples is stated without blaming a limit that was never reached.
 */
function capNote(shown: number, exampleCount: number): string | null {
  if (exampleCount <= shown) return null;
  if (shown >= DETAIL_CAP) {
    return `Showing ${shown} of the ${exampleCount} examples on the latest discovery run. Opening a trend returns at most six of them, so this is not the complete set.`;
  }
  return `Showing ${shown} of the ${exampleCount} examples on the latest discovery run, so this is not the complete set.`;
}

/**
 * The track and the slides, and nothing else. Memoised on the SETTLED position:
 * the carousel re-renders on every intermediate scroll position so the bars
 * stay live, and without this six slides would reconcile on each of those
 * frames. `activeIndex` is the carousel's own playback gate, so one video
 * starts, at the destination, instead of five starting and stopping on the way.
 */
const ExampleTrack = memo(function ExampleTrack({
  examples,
  count,
  activeIndex,
  paused,
  standIn,
  trackRef,
}: {
  examples: TrendExample[];
  count: number;
  activeIndex: number;
  /** The stage's Pause is pressed. */
  paused: boolean;
  standIn?: PlateArt;
  trackRef: RefObject<HTMLDivElement | null>;
}) {
  return (
    <div
      ref={trackRef}
      className="tsc-track"
      tabIndex={0}
      role="group"
      aria-roledescription="carousel"
      aria-label={`${count} ${count === 1 ? "example" : "examples"}, use the left and right arrow keys to move between them`}
    >
      {examples.map((example, i) => (
        <div
          key={example.id}
          className="tsc-slide"
          role="group"
          aria-roledescription="slide"
          aria-label={`Example ${i + 1} of ${count}`}
        >
          {/* The same media resolver the wall plates go through, so a refetch
              that mints fresh presigned URLs clears a stale failure flag here
              too. Motion is the stage's own Pause, not the wall's floor: it
              starts pressed under reduced motion, and the viewer may lift it. */}
          <div className="trend-plate tsc-plate">
            <PlateMedia
              videoUrl={example.videoUrl}
              videoBroken={example.videoBroken}
              thumbnailUrl={example.thumbnailUrl}
              standIn={standIn}
              scale="stage"
              active={i === activeIndex}
              /* Keyed on the example, so a refetch that re-signs its URLs
                 updates this slide's video rather than rebuilding it. */
              mediaId={example.id}
              viewerPaused={paused}
            />
          </div>
        </div>
      ))}
    </div>
  );
});

/** One counted figure on the example: the value, and under it what it counts. */
function ExampleFigure({ label, value }: { label: string; value: number | null }) {
  return (
    <div className="td-fig">
      <dt className="td-label">{label}</dt>
      <dd className="td-count">
        {value === null ? (
          <span className="td-label">Not counted</span>
        ) : (
          <>
            <span aria-hidden="true">{compactTrendNum(value)}</span>
            <span className="sr-only">{spokenTrendNum(value)}</span>
          </>
        )}
      </dd>
    </div>
  );
}

/**
 * THIS EXAMPLE: whose video is on the stage, where to open it, what its creator
 * wrote, and what it was counted at. Memoised on the SETTLED example, which is
 * what keeps a swipe quiet: this block is rewritten once, on landing.
 */
const ExampleFacts = memo(function ExampleFacts({
  example,
  note,
}: {
  example: TrendExample;
  /** The cap note: the carousel is not the complete set. */
  note: string | null;
}) {
  const platform = platformLabel(example.platform);
  const headingId = useId();

  return (
    <section aria-labelledby={headingId}>
      <h3 id={headingId} className="td-rule">
        This example
      </h3>

      {/* The cap, at the head of the block the carousel drives. Here rather
          than under the stage, so it never changes the stage column's height,
          which is the sheet's. */}
      {note ? <p className="td-note">{note}</p> : null}

      <div className="td-example-head">
        <p className="td-creator">
          {hasGlyph(example.platform) ? (
            <PlatformGlyph platform={example.platform.toUpperCase()} className="size-3.5 shrink-0" />
          ) : null}
          {/* A handle is a literal string, so it is never uppercased, and a
              missing one is said rather than filled in. */}
          {example.authorHandle ? (
            <span className="td-handle">@{example.authorHandle}</span>
          ) : (
            <span className="td-label">Creator handle not captured</span>
          )}
          <span className="sr-only">, on {platform}</span>
        </p>

        {example.pageUrl ? (
          <a
            href={example.pageUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="td-link"
          >
            <ArrowUpRight className="size-3.5" aria-hidden="true" />
            Open<span className="sr-only"> on {platform}</span>
          </a>
        ) : (
          <span className="td-label">No link captured</span>
        )}
      </div>

      {example.caption ? (
        /* dir="auto": a creator's caption is often Arabic, and it reads from
           the right in its own paragraph without turning the sheet around. */
        <p dir="auto" className="td-caption line-clamp-4">
          {example.caption}
        </p>
      ) : (
        <p className="td-caption td-caption--missing">No caption captured</p>
      )}

      {/* The counts, and immediately whose they are: ON THIS VIDEO sits on the
          same line, so these can never be read as the trend's. */}
      <div className="td-counted">
        <dl className="flex flex-wrap items-end gap-x-6 gap-y-2">
          <ExampleFigure label="Views" value={example.viewCount} />
          <ExampleFigure label="Likes" value={example.likeCount} />
        </dl>
        <p className="td-label">On this video</p>
      </div>
    </section>
  );
});

export default function TrendSubmissionCarousel({
  examples,
  exampleCount,
  standIn,
  lead,
  tail,
}: {
  examples: TrendExample[];
  /** The trend's own aggregate, which can be larger than what the route sent. */
  exampleCount: number;
  standIn?: PlateArt;
  /** The reading column above THIS EXAMPLE: title, reading, tags, actions and
   *  EVIDENCE. Built once by the panel. */
  lead: ReactNode;
  /** The reading column under THIS EXAMPLE: WHERE IT WAS FOUND. */
  tail: ReactNode;
}) {
  const count = examples.length;
  const trackRef = useRef<HTMLDivElement>(null);
  /* Where the finger or the smooth scroll is RIGHT NOW. It moves the bars and
     the counter and spends the arrows at the ends, and nothing else reads it. */
  const [position, setPosition] = useState(0);
  /* Where the carousel came to rest. Everything that describes the example is
     drawn from this one, so a four-slide swipe rewrites it once. */
  const [landed, setLanded] = useState(0);
  /* The position, mirrored outside React's render cycle, so two clicks on Next
     inside one frame advance twice rather than both resolving to one slide. */
  const indexRef = useRef(0);
  const at = Math.min(position, count - 1);
  const settled = Math.min(landed, count - 1);
  const active = examples[settled];

  const goTo = useCallback(
    (next: number) => {
      const clamped = Math.max(0, Math.min(count - 1, next));
      indexRef.current = clamped;
      setPosition(clamped);
      const el = trackRef.current;
      if (!el || !el.clientWidth) {
        /* Nothing to scroll, so nothing will ever report a landing: commit it
           here rather than leaving the reading on an example the viewer left. */
        setLanded(clamped);
        return;
      }
      /* No behavior of our own: `auto` defers to the stylesheet, which is only
         smooth where reduced motion was not asked for. */
      el.scrollTo({ left: clamped * el.clientWidth });
    },
    [count],
  );

  const step = useCallback((delta: number) => goTo(indexRef.current + delta), [goTo]);

  /* The finger reports through scroll position. scrollend is the platform
     saying the scroll is over; the timer is the same statement inferred where
     scrollend does not exist, and the equality guards make the second of the
     two no re-render at all. Math.abs covers a right-to-left writing mode. */
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    let timer = 0;

    const read = () => {
      const width = el.clientWidth;
      if (!width) return indexRef.current;
      return Math.max(0, Math.min(count - 1, Math.round(Math.abs(el.scrollLeft) / width)));
    };
    const settle = () => {
      const next = read();
      indexRef.current = next;
      setPosition((cur) => (cur === next ? cur : next));
      setLanded((cur) => (cur === next ? cur : next));
    };
    const onScroll = () => {
      const next = read();
      indexRef.current = next;
      setPosition((cur) => (cur === next ? cur : next));
      window.clearTimeout(timer);
      timer = window.setTimeout(settle, SETTLE_MS);
    };

    el.addEventListener("scroll", onScroll, { passive: true });
    el.addEventListener("scrollend", settle);
    return () => {
      window.clearTimeout(timer);
      el.removeEventListener("scroll", onScroll);
      el.removeEventListener("scrollend", settle);
    };
  }, [count]);

  /* Left and right move the carousel, Home and End reach its ends, and nothing
     else is touched: Escape, Tab, J, K and the sheet's own handling pass
     straight through. Bound on the stage column only, so arrow keys pressed
     while reading text stay the browser's. */
  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLElement>) => {
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        step(-1);
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        step(1);
      } else if (e.key === "Home") {
        e.preventDefault();
        goTo(0);
      } else if (e.key === "End") {
        e.preventDefault();
        goTo(count - 1);
      }
    },
    [count, goTo, step],
  );

  /* From md up the reading column is its own scroller beside the stage (the
     sheet's height is the stage's, trendWall.css), so a wheel turned over the
     stage scrolls the reading, as it did when the whole body scrolled. Only a
     vertical turn: a sideways one belongs to the track. */
  const readingRef = useRef<HTMLDivElement>(null);
  const onStageWheel = useCallback((e: WheelEvent<HTMLElement>) => {
    const reading = readingRef.current;
    if (!reading || Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
    if (reading.scrollHeight > reading.clientHeight) reading.scrollTop += e.deltaY;
  }, []);

  const note = capNote(count, exampleCount);
  const many = count > 1;
  /* Sound belongs to the sheet, not to a slide: moving between examples must not
     silently re-mute what the viewer just asked to hear. */
  const { soundOn, toggleSound, reduced, paused: wallPaused } = usePlayback();
  /* THE STAGE'S OWN PAUSE (WCAG 2.2.2). It holds whichever example is on the
     stage, and it stays pressed while the viewer moves between examples,
     because stopping the motion was asked of the stage and not of one clip.
     Under reduced motion it starts pressed, so nothing moves until the viewer
     asks, and it presses itself if reduced motion is turned on while the sheet
     is open. Adjusted while rendering, the pattern React documents for
     resetting state when an input changes. */
  const [stagePaused, setStagePaused] = useState(reduced || wallPaused);
  const [reducedWas, setReducedWas] = useState(reduced);
  if (reduced !== reducedWas) {
    setReducedWas(reduced);
    if (reduced) setStagePaused(true);
  }
  const hasVideo = Boolean(active?.videoUrl) && !active?.videoBroken;
  /* Nothing is playing, so there is nothing to hear and a chip would be a lie.
     Held still covers the viewer's Pause here and the wall's pause. */
  const canHear = hasVideo && !stagePaused && !wallPaused;

  return (
    <div className="td-layout">
      <section
        aria-label="Examples behind this trend"
        className="td-stage-col"
        onKeyDown={onKeyDown}
        onWheel={onStageWheel}
      >
        <div className="tsc-frame">
          <ExampleTrack
            examples={examples}
            count={count}
            activeIndex={settled}
            paused={stagePaused}
            standIn={standIn}
            trackRef={trackRef}
          />

          {/* The platform of the example on the stage, in the wall plate's
              corner. One mark, because the stage shows one video: a TikTok
              clip wearing an Instagram chip would be the trend's platforms
              printed on something that is not the trend. */}
          <PlatformBadge
            platform={active.platform}
            label={platformLabel(active.platform)}
            surface="media"
            className="tsc-mark"
          />

          {/* Pause, bottom left, whenever the example on the stage is a
              video: the one control that stops the motion, so it never
              moves and never goes. Its name is fixed and aria-pressed carries
              the state; the glyph shows what pressing it will do. */}
          {hasVideo ? (
            <button
              type="button"
              className="co-media-chip tsc-pause"
              onClick={() => setStagePaused((held) => !held)}
              aria-pressed={stagePaused}
              aria-label="Pause"
            >
              {stagePaused ? (
                <Play className="size-[13px]" strokeWidth={2.25} aria-hidden="true" />
              ) : (
                <Pause className="size-[13px]" strokeWidth={2.25} aria-hidden="true" />
              )}
            </button>
          ) : null}

          {canHear ? (
            <button
              type="button"
              className="co-media-chip tsc-sound"
              onClick={toggleSound}
              aria-pressed={soundOn}
              /* A fixed name, and aria-pressed says whether it is on: a name
                 that flipped as well would state the state twice. */
              aria-label="Sound"
            >
              {soundOn ? (
                <Volume2 className="size-[13px]" strokeWidth={2.25} aria-hidden="true" />
              ) : (
                <VolumeX className="size-[13px]" strokeWidth={2.25} aria-hidden="true" />
              )}
            </button>
          ) : null}
        </div>

        <div className="tsc-controls">
          <div className="tsc-where">
            {many ? (
              <div className="tsc-dots">
                {examples.map((example, i) => (
                  <button
                    key={example.id}
                    type="button"
                    className="tsc-dotbtn"
                    onClick={() => goTo(i)}
                    aria-current={i === at ? "true" : undefined}
                    aria-label={`Show example ${i + 1} of ${count}${
                      example.authorHandle ? `, ${example.authorHandle}` : ""
                    }, on ${platformLabel(example.platform)}`}
                  >
                    <span className={cn("tsc-dot", i === at && "tsc-dot--on")} aria-hidden="true" />
                  </button>
                ))}
              </div>
            ) : null}
            {/* Said in words as well as drawn, and never rounded off: the bars
                say where you are, only this says how many there are. */}
            <span className="td-label whitespace-nowrap" aria-hidden="true">
              Example {at + 1} of {count}
            </span>
          </div>

          {many ? (
            <div className="tsc-steps">
              <button
                type="button"
                className="tsc-step"
                onClick={() => step(-1)}
                disabled={at === 0}
                aria-label="Previous example"
              >
                <ChevronLeft className="size-4" aria-hidden="true" />
              </button>
              <button
                type="button"
                className="tsc-step"
                onClick={() => step(1)}
                disabled={at === count - 1}
                aria-label="Next example"
              >
                <ChevronRight className="size-4" aria-hidden="true" />
              </button>
            </div>
          ) : null}
        </div>
      </section>

      <div ref={readingRef} className="td-reading">
        {lead}
        <ExampleFacts example={active} note={note} />
        {tail}
      </div>

      {/* Politely, only the position, and only once the carousel has landed. */}
      <p className="sr-only" role="status">
        Example {settled + 1} of {count}
        {active.authorHandle ? `, ${active.authorHandle}` : ""}, on {platformLabel(active.platform)}
      </p>
    </div>
  );
}
